import type { EnhancerEntrySurface } from '@/analytics/enhancer-types';

const ENHANCER_CONVERSION_CONTEXT_KEY =
  'imgenhancer:enhancer_conversion_context';
const ENHANCER_CONVERSION_CONTEXT_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export type EnhancerConversionContext = {
  conversion_source: 'enhancer';
  enhancer_session_id: string;
  enhancer_tool_id: string | null;
  enhancer_entry_surface: EnhancerEntrySurface | null;
  source_ui_surface: string | null;
  created_at_ms: number;
};

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isValidEntrySurface(value: unknown): value is EnhancerEntrySurface {
  return (
    value === 'home' ||
    value === 'tool_page' ||
    value === 'pricing' ||
    value === 'settings' ||
    value === 'other'
  );
}

export function persistEnhancerConversionContext(params: {
  enhancerSessionId: string;
  toolId?: string | null;
  entrySurface?: EnhancerEntrySurface | null;
  sourceUiSurface?: string | null;
}) {
  if (typeof window === 'undefined') return;

  const enhancerSessionId = normalizeString(params.enhancerSessionId);
  if (!enhancerSessionId) return;

  const payload: EnhancerConversionContext = {
    conversion_source: 'enhancer',
    enhancer_session_id: enhancerSessionId,
    enhancer_tool_id: normalizeString(params.toolId ?? null),
    enhancer_entry_surface: isValidEntrySurface(params.entrySurface)
      ? params.entrySurface
      : null,
    source_ui_surface: normalizeString(params.sourceUiSurface ?? null),
    created_at_ms: Date.now(),
  };

  try {
    window.sessionStorage.setItem(
      ENHANCER_CONVERSION_CONTEXT_KEY,
      JSON.stringify(payload)
    );
  } catch {
    // Ignore storage failures.
  }
}

export function clearEnhancerConversionContext() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ENHANCER_CONVERSION_CONTEXT_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function readEnhancerConversionContext(): EnhancerConversionContext | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(ENHANCER_CONVERSION_CONTEXT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<EnhancerConversionContext> | null;
    if (!parsed || parsed.conversion_source !== 'enhancer') {
      clearEnhancerConversionContext();
      return null;
    }

    const createdAtMs =
      typeof parsed.created_at_ms === 'number' &&
      Number.isFinite(parsed.created_at_ms)
        ? parsed.created_at_ms
        : null;
    if (!createdAtMs) {
      clearEnhancerConversionContext();
      return null;
    }

    if (Date.now() - createdAtMs > ENHANCER_CONVERSION_CONTEXT_MAX_AGE_MS) {
      clearEnhancerConversionContext();
      return null;
    }

    const enhancerSessionId = normalizeString(parsed.enhancer_session_id);
    if (!enhancerSessionId) {
      clearEnhancerConversionContext();
      return null;
    }

    return {
      conversion_source: 'enhancer',
      enhancer_session_id: enhancerSessionId,
      enhancer_tool_id: normalizeString(parsed.enhancer_tool_id),
      enhancer_entry_surface: isValidEntrySurface(parsed.enhancer_entry_surface)
        ? parsed.enhancer_entry_surface
        : null,
      source_ui_surface: normalizeString(parsed.source_ui_surface),
      created_at_ms: createdAtMs,
    };
  } catch {
    clearEnhancerConversionContext();
    return null;
  }
}
